import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { TradePlanInput } from 'shared';

import { createTradePlan, executeTradePlan, recheckTradePlan } from '../../lib/api/endpoints';
import { notificationKeys } from '../notifications/queries';
import { notify } from '../notifications/toasts';

import { tradePlanKeys } from './queries';

export function useCreateTradePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TradePlanInput) => createTradePlan(input),
    onSuccess: async (result, input) => {
      const reason = result.gateDecision.reasons[0];
      if (result.gateDecision.decision === 'BLOCKED') {
        notify({
          kind: 'TRADE_PLAN_BLOCKED',
          symbol: input.symbol,
          variant: 'error',
          title: `Trade plan blocked - ${input.symbol.toUpperCase()}`,
          description: reason,
          block: true,
        });
      } else if (result.gateDecision.decision === 'WARN') {
        notify({
          kind: 'TRADE_PLAN_WARN',
          symbol: input.symbol,
          variant: 'warning',
          title: `Trade plan warning - ${input.symbol.toUpperCase()}`,
          description: reason,
        });
      } else {
        notify({
          kind: 'TRADE_PLAN_APPROVED',
          symbol: input.symbol,
          variant: 'success',
          title: `Trade plan approved - ${input.symbol.toUpperCase()}`,
        });
      }
      await queryClient.invalidateQueries({ queryKey: tradePlanKeys.all });
      await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useRecheckTradePlan() {
  return useMutation({
    mutationFn: (id: string) => recheckTradePlan(id),
  });
}

export function useExecuteTradePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => executeTradePlan(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tradePlanKeys.all });
    },
  });
}
